/**
 * Centralized Pricing Configuration (Frontend)
 *
 * Single source of truth for all product plans, prices, and subscription tiers.
 * All UI components should import from here instead of hardcoding prices.
 */

export const PLANS = {
  // --- One-time QR Sticker Products ---
  individual: {
    id: "individual",
    name: "Individual",
    description: "1 QR Code ID with 2 stickers (1x1 inch)",
    price: 99,
    currency: "INR",
    type: "one_time",
    qrCodeCount: 1,
    stickerCount: 2,
    includesPremiumMonths: 12,
    badge: null,
    features: [
      "1 unique QR Code ID",
      "2 weatherproof stickers",
      "1 year premium features free",
      "Unlimited emergency info fields",
      "Free shipping",
    ],
  },
  family: {
    id: "family",
    name: "Family Pack",
    description: "4 QR Code IDs with 8 stickers (1x1 inch)",
    price: 249,
    currency: "INR",
    type: "one_time",
    qrCodeCount: 4,
    stickerCount: 8,
    includesPremiumMonths: 12,
    badge: "Best Value",
    features: [
      "4 unique QR Code IDs",
      "8 weatherproof stickers (2 per ID)",
      "1 year premium features free",
      "Protect parents, kids & yourself",
      "Free shipping",
    ],
  },

  // --- Subscriptions (Premium Features) ---
  premium_yearly: {
    id: "premium_yearly",
    name: "Premium",
    description: "Advanced features for your emergency QR code",
    price: 199,
    currency: "INR",
    type: "subscription",
    interval: "yearly",
    maxLockets: 1,
    badge: null,
    features: [
      "Unlimited info fields",
      "Scan notifications",
      "Document upload",
      "Offline caching",
      "Priority support",
    ],
  },
  family_yearly: {
    id: "family_yearly",
    name: "Family Premium",
    description: "Premium features for up to 4 QR Code IDs",
    price: 499,
    currency: "INR",
    type: "subscription",
    interval: "yearly",
    maxLockets: 4,
    badge: "Best Value",
    features: [
      "Unlimited info fields (4 QR IDs)",
      "Scan notifications",
      "Document upload",
      "Offline caching",
      "Priority support",
    ],
  },

  // --- Future Physical Products ---
  dog_tag_locket: {
    id: "dog_tag_locket",
    name: "QR Dog Tag Locket",
    description: "Stainless steel dog tag with chain and engraved QR code",
    price: 999,
    currency: "INR",
    type: "one_time",
    qrCodeCount: 1,
    stickerCount: 0,
    includesPremiumMonths: 12,
    badge: "Coming Soon",
    active: false,
    features: [
      "Stainless steel dog tag",
      "Chain included",
      "Laser-engraved QR code",
      "1 year premium features free",
      "Premium packaging",
    ],
  },
};

// Active plans available for purchase (Family Pack first, then Individual, then others)
const productPlans = Object.values(PLANS).filter(
  (p) => p.type === "one_time" && p.active !== false
);
const FAMILY_FIRST_ORDER = { family: 0, individual: 1 };
export const ACTIVE_PRODUCT_PLANS = [...productPlans].sort(
  (a, b) => (FAMILY_FIRST_ORDER[a.id] ?? 2) - (FAMILY_FIRST_ORDER[b.id] ?? 2)
);

const subscriptionPlans = Object.values(PLANS).filter((p) => p.type === "subscription");
export const ACTIVE_SUBSCRIPTION_PLANS = [...subscriptionPlans].sort(
  (a, b) => (a.id === "family_yearly" ? 0 : 1) - (b.id === "family_yearly" ? 0 : 1)
);

// Helper to format price for display
export const formatPrice = (price) => `₹${price}`;

// Default plan for the primary CTA across the site
export const DEFAULT_PLAN = PLANS.individual;

// Price display string for buttons (e.g., "₹99")
export const CTA_PRICE = formatPrice(DEFAULT_PLAN.price);
